<!-- 📊 Base Chart Component para MGI -->
<template>
  <v-card
    :class="[
      'mgi-chart-container',
      { 'chart-loading': loading }
    ]"
    :elevation="elevation"
    :variant="variant as any"
  >
    <!-- 📋 Header opcional -->
    <v-card-title v-if="title || $slots.header || showRefresh || showDownload || showOptions" class="chart-header">
      <slot name="header">
        <div class="d-flex align-center justify-between">
          <div class="chart-title">
            <v-icon
              v-if="icon"
              class="me-2"
              :color="iconColor"
            >
              {{ icon }}
            </v-icon>
            <span v-if="title">{{ title }}</span>
          </div>
          
          <!-- 🔄 Acciones del gráfico -->
          <div class="chart-actions">
            <v-btn
              v-if="showRefresh"
              icon
              size="small"
              variant="text"
              @click="refreshChart"
              :loading="loading"
              data-testid="refresh-button"
            >
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            
            <v-btn
              v-if="showDownload"
              icon
              size="small"
              variant="text"
              @click="downloadChart"
              data-testid="download-button"
            >
              <v-icon>mdi-download</v-icon>
            </v-btn>
            
            <v-menu v-if="showOptions">
              <template v-slot:activator="{ props }">
                <v-btn
                  icon
                  size="small"
                  variant="text"
                  v-bind="props"
                  data-testid="options-button"
                >
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              
              <v-list>
                <v-list-item @click="exportToPNG">
                  <v-list-item-title>Exportar PNG</v-list-item-title>
                </v-list-item>
                <v-list-item @click="exportToSVG">
                  <v-list-item-title>Exportar SVG</v-list-item-title>
                </v-list-item>
                <v-list-item @click="copyToClipboard">
                  <v-list-item-title>Copiar al portapapeles</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </slot>
    </v-card-title>

    <!-- 📊 Contenido del gráfico -->
    <v-card-text class="chart-content">
      <!-- 🔄 Skeleton loader -->
      <SkeletonLoader
        v-if="loading"
        type="chart"
        :height="height"
        data-testid="skeleton-loader"
      />
      
      <!-- 📊 Canvas del gráfico -->
      <div v-else class="chart-wrapper" :style="{ height: `${height}px` }">
        <canvas
          ref="chartCanvas"
          :id="chartId"
          :style="{ maxHeight: `${height}px` }"
          role="img"
          :aria-label="`${title || 'Chart'} - ${chartType} chart`"
        ></canvas>
      </div>
      
      <!-- ❌ Estado de error -->
      <div v-if="error && !loading" class="chart-error">
        <v-alert
          type="error"
          variant="tonal"
          icon="mdi-chart-line-variant"
        >
          <v-alert-title>Error al cargar el gráfico</v-alert-title>
          {{ error }}
        </v-alert>
      </div>
      
      <!-- 📭 Estado vacío -->
      <div v-if="!hasData && !loading && !error" class="chart-empty">
        <v-alert
          type="info"
          variant="tonal"
          icon="mdi-chart-box-outline"
        >
          <v-alert-title>Sin datos disponibles</v-alert-title>
          {{ emptyMessage }}
        </v-alert>
      </div>
    </v-card-text>

    <!-- 📋 Footer opcional -->
    <v-card-actions v-if="$slots.footer">
      <slot name="footer" />
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick, computed, watch } from 'vue'
import { Chart, type ChartConfiguration, type ChartType } from 'chart.js/auto'
import SkeletonLoader from '@/components/ui/SkeletonLoader.vue'
import { NotificationService } from '@/services/notification.service'

// 🏷️ Props
interface Props {
  title?: string
  icon?: string
  iconColor?: string
  height?: number
  chartType: ChartType
  data: any
  options?: any
  loading?: boolean
  error?: string
  emptyMessage?: string
  elevation?: number
  variant?: 'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain'
  showRefresh?: boolean
  showDownload?: boolean
  showOptions?: boolean
  responsive?: boolean
  maintainAspectRatio?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  height: 400,
  elevation: 2,
  variant: 'elevated',
  emptyMessage: 'No hay datos para mostrar',
  showRefresh: true,
  showDownload: false,
  showOptions: false,
  responsive: true,
  maintainAspectRatio: false
})

// 🎯 Emits
const emit = defineEmits<{
  refresh: []
  download: []
  chartCreated: [chart: Chart]
  chartUpdated: [chart: Chart]
  chartError: [error: string]
}>()

// 📊 Referencias
const chartCanvas = ref<HTMLCanvasElement>()
const chartInstance = ref<Chart | null>(null)

// 🎯 Computed
const chartId = computed(() => `chart-${Math.random().toString(36).substr(2, 9)}`)

const hasData = computed(() => {
  if (!props.data) return false
  if (Array.isArray(props.data.datasets)) {
    return props.data.datasets.some((dataset: any) => 
      dataset.data && dataset.data.length > 0
    )
  }
  return false
})

const chartConfig = computed((): ChartConfiguration => ({
  type: props.chartType,
  data: props.data,
  options: {
    responsive: props.responsive,
    maintainAspectRatio: props.maintainAspectRatio,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          padding: 20,
          usePointStyle: true
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0,0,0,0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1
      }
    },
    scales: props.chartType !== 'pie' && props.chartType !== 'doughnut' ? {
      x: {
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        }
      },
      y: {
        grid: {
          display: true,
          color: 'rgba(0,0,0,0.1)'
        },
        beginAtZero: true
      }
    } : undefined,
    ...props.options
  }
}))

// 🎯 Métodos
const createChart = async () => {
  if (!chartCanvas.value || props.loading || !hasData.value) return
  
  try {
    // 🗑️ Destruir gráfico anterior
    if (chartInstance.value) {
      chartInstance.value.destroy()
    }
    
    await nextTick()
    
    // 📊 Crear nuevo gráfico
    chartInstance.value = new Chart(chartCanvas.value, chartConfig.value)
    
    emit('chartCreated', chartInstance.value)
    
  } catch (error) {
    console.error('Error creating chart:', error)
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
    emit('chartError', errorMessage)
    NotificationService.error(`Error al crear el gráfico: ${errorMessage}`)
  }
}

const updateChart = () => {
  if (!chartInstance.value || props.loading) return
  
  try {
    // 📊 Actualizar datos
    chartInstance.value.data = props.data
    if (chartConfig.value.options) {
      chartInstance.value.options = chartConfig.value.options
    }
    chartInstance.value.update('active')
    
    emit('chartUpdated', chartInstance.value)
    
  } catch (error) {
    console.error('Error updating chart:', error)
    const errorMessage = error instanceof Error ? error.message : 'Error desconocido'
    emit('chartError', errorMessage)
  }
}

const refreshChart = () => {
  emit('refresh')
}

const downloadChart = () => {
  if (!chartInstance.value) {
    emit('download')
    return
  }
  
  try {
    const url = chartInstance.value.toBase64Image()
    const link = document.createElement('a')
    link.download = `${props.title || 'chart'}.png`
    link.href = url
    link.click()
    
    NotificationService.success('Gráfico descargado exitosamente')
    emit('download')
    
  } catch (error) {
    console.error('Error downloading chart:', error)
    NotificationService.error('Error al descargar el gráfico')
    emit('download') // Emit even on error for consistency
  }
}

const exportToPNG = () => {
  downloadChart()
}

const exportToSVG = () => {
  // TODO: Implementar exportación SVG
  NotificationService.info('Exportación SVG no disponible aún')
}

const copyToClipboard = async () => {
  if (!chartInstance.value) return
  
  try {
    const canvas = chartInstance.value.canvas
    canvas.toBlob(async (blob) => {
      if (blob) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ])
        NotificationService.success('Gráfico copiado al portapapeles')
      }
    })
  } catch (error) {
    console.error('Error copying chart:', error)
    NotificationService.error('Error al copiar el gráfico')
  }
}

// 👀 Watchers
watch(() => props.data, () => {
  if (chartInstance.value && hasData.value) {
    updateChart()
  } else if (hasData.value) {
    createChart()
  }
}, { deep: true })

watch(() => props.loading, (loading) => {
  if (!loading && hasData.value) {
    nextTick(() => createChart())
  }
})

// 🔄 Lifecycle
onMounted(() => {
  if (hasData.value && !props.loading) {
    nextTick(() => createChart())
  }
})

onUnmounted(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
  }
})

// 🔧 Exposer métodos para testing
defineExpose({
  chartInstance,
  createChart,
  updateChart,
  refreshChart,
  downloadChart,
  exportToPNG,
  exportToSVG,
  copyToClipboard
})
</script>

<style scoped>
.mgi-chart-container {
  position: relative;
  overflow: hidden;
}

.chart-header {
  border-bottom: 1px solid rgba(var(--v-border-color), 0.12);
  padding: 16px 20px;
}

.chart-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1.1rem;
}

.chart-actions {
  display: flex;
  gap: 4px;
}

.chart-content {
  padding: 20px;
}

.chart-wrapper {
  position: relative;
  width: 100%;
}

.chart-loading {
  opacity: 0.7;
}

.chart-error,
.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* 🌟 Animaciones */
.mgi-chart-container {
  transition: all 0.3s ease;
}

.chart-wrapper canvas {
  transition: opacity 0.3s ease;
}

.chart-loading .chart-wrapper canvas {
  opacity: 0.5;
}

/* 📱 Responsive */
@media (max-width: 600px) {
  .chart-header {
    padding: 12px 16px;
  }
  
  .chart-content {
    padding: 16px;
  }
  
  .chart-title {
    font-size: 1rem;
  }
}
</style>
